import { Controller, useWatch } from "react-hook-form";
import { Label } from "../../../../components/ui/label";
import { Checkbox } from "../../../../components/ui/checkbox";
import { FieldError } from "../FieldError";
import { useEffect } from "react";
import {
  ACTIVE_SUPPORTED_CARRIERS,
  ACTIVE_SUPPORTED_CARRIER_IDS,
} from "helpers/carriers";

const ACTIVE_CARRIER_IDS = new Set(ACTIVE_SUPPORTED_CARRIER_IDS);

export function CarrierPicker({ control, watch, setValue, getValues }) {
  const carriers = watch("step1.carrier");
  const allowedCarries = watch("step1.allowedCarriers");
  const garageState = watch("step1.garageState");
  const hazmat = watch("step1.hazmatPlacard");
  const powerUnits = watch("step1.powerUnits");
  const showAccelerant = typeof powerUnits === "number" && powerUnits >= 1 && powerUnits <= 19;
  const visibleAllowedCarriers = Array.isArray(allowedCarries)
    ? allowedCarries.filter((carrier) => carrier !== "Accelerant" || showAccelerant)
        .filter((carrier) => ACTIVE_CARRIER_IDS.has(carrier))
    : [];

  // Keep selected carriers in sync once allowedCarriers has loaded.
  useEffect(() => {
    if (!Array.isArray(carriers)) return;
    if (!visibleAllowedCarriers.length) return;

    const filtered = carriers.filter((c) => visibleAllowedCarriers.includes(c));
    if (JSON.stringify(filtered) !== JSON.stringify(carriers)) {
      setValue("step1.carrier", filtered, { shouldValidate: true });
    }
  }, [visibleAllowedCarriers, carriers, setValue]);

  // Existing logic
  useEffect(() => {
    if (!Array.isArray(carriers)) return;

    const hasCW = carriers.includes("Cover Whale");
    const hasProg = carriers.includes("Progressive");
    const hasNir = carriers.includes("Nirvana");
    const progOnly = hasProg && !hasCW && !hasNir;

    // Progressive-only → clear both fields
    if (progOnly) {
      setValue("step4.telemetryOption", undefined, { shouldValidate: false });
      setValue("step4.telemetryProvider", undefined, { shouldValidate: false });
      return;
    }

    if (!hasProg) {
      setValue("step1.insuredWithProgressiveCommercialAuto", "na", {
        shouldValidate: false,
      });
    }

    if (!hasCW) {
      setValue("step4.telemetryOption", undefined, { shouldValidate: false });
    }

    if (!hasNir) {
      setValue("step4.telemetryProvider", undefined, { shouldValidate: false });
      setValue("step1.operatedTwoYears", "na", { shouldValidate: false });
    }

    if (hasNir) {
      const provider = getValues("step4.telemetryProvider");
      if (provider === "") {
        setValue("step4.telemetryProvider", undefined, {
          shouldValidate: false,
        });
      }
    }

    if (hasCW) {
      const option = getValues("step4.telemetryOption");
      if (option === "") {
        setValue("step4.telemetryOption", undefined, { shouldValidate: false });
      }
    }
  }, [carriers, setValue, getValues]);

  return (
    <div className="space-y-3">
      {visibleAllowedCarriers.length ? (
        <>
          <Label>Select from the below carriers:</Label>

          <Controller
            control={control}
            name="step1.carrier"
            render={({ field }) => {
              const value = field.value || [];
              const toggle = (id) =>
                value.includes(id)
                  ? field.onChange(value.filter((v) => v !== id))
                  : field.onChange([...value, id]);

              return (
                <div className="flex flex-wrap gap-8">
                  {ACTIVE_SUPPORTED_CARRIERS.filter((c) => visibleAllowedCarriers.includes(c.id)).map((c) => {
                    const itemId = `carrier-${c.id}`;
                    return (
                      <div key={c.id} className="flex items-start gap-2">
                        <Checkbox
                          id={itemId}
                          checked={value.includes(c.id)}
                          onCheckedChange={() => {
                            toggle(c.id);
                            if (c.id === "Progressive") {
                              setValue("step4.telemetryOption", undefined);
                              setValue("step4.telemetryProvider", undefined);
                            } else if (c.id === "Cover Whale") {
                              setValue("step4.telemetryProvider", undefined);
                            }
                          }}
                          className={[
                            "data-[state=checked]:!bg-transparent data-[state=checked]:!text-inherit",
                            "data-[state=checked]:border-black",
                            "[&>span[data-slot='checkbox-indicator']]:hidden",
                            "[&>span[data-slot='checkbox-indicator']>svg]:hidden",
                            "after:content-[''] after:absolute after:top-[1px] after:left-[1px]",
                            "after:h-3 after:w-3 after:rounded-[3px] after:bg-red-600",
                            "after:opacity-0 data-[state=checked]:after:opacity-100",
                            "relative h-4 w-4 shrink-0 rounded-[4px] border border-gray-300",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-600",
                          ].join(" ")}
                          disabled={hazmat && c.id !== "Accelerant"}
                        />

                        <Label
                          htmlFor={itemId}
                          className="cursor-pointer select-none leading-tight"
                        >
                          <div className="font-medium">{c.label}</div>
                          <div className="text-xs text-gray-500">{c.sub}</div>
                        </Label>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />

          <FieldError name="step1.carrier" step="step1" control={control} />
        </>
      ) : (
        <div className="text-sm text-gray-500">
          {!garageState
            ? "Please select a garage state to see available carriers."
            : powerUnits === undefined || powerUnits === null
            ? "Please enter the number of power units to see available carriers."
            : garageState
            ? "No carriers are available for the selected garage state."
            : "Please select a garage state to see available carriers."}
        </div>
      )}
    </div>
  );
}
